import type { ModuleGridType, StudioSystemType } from 'types/studio/items'

/** Stable uuid sort (matches suitability / BOM field order). */
export function sortVanDerValkModuleGrids(grids: ModuleGridType[]): ModuleGridType[] {
  return [...grids]
    .map((grid, originalIndex) => ({ grid, originalIndex }))
    .sort((a, b) => {
      const ua = String(a.grid.uuid ?? '')
      const ub = String(b.grid.uuid ?? '')
      if (ua !== ub) {
        return ua.localeCompare(ub, undefined, { numeric: true })
      }
      return a.originalIndex - b.originalIndex
    })
    .map(({ grid }) => grid)
}

/** Module grids with at least one active cell, uuid-sorted. */
export function getVanDerValkModuleGridsForBom(system: StudioSystemType | null | undefined): ModuleGridType[] {
  if (!system) {
    return []
  }
  const grids = system.moduleGrids()
  if (!grids?.length) {
    return []
  }
  return sortVanDerValkModuleGrids(grids).filter((grid) => {
    const cells = grid.cellsActive
    return Array.isArray(cells) && cells.length > 0
  })
}

/** Group active BOM grids by roof facet uuid (vendor pitched BOM: one roof plane per request). */
export function groupVanDerValkModuleGridsByFacet(grids: ModuleGridType[]): Map<string, ModuleGridType[]> {
  const groups = new Map<string, ModuleGridType[]>()
  for (const grid of grids) {
    const facet = grid.facet as { uuid?: string } | null | undefined
    const key = facet?.uuid != null ? String(facet.uuid) : `__grid_${grid.uuid ?? groups.size}`
    const list = groups.get(key) ?? []
    list.push(grid)
    groups.set(key, list)
  }
  return groups
}

/** First grid with valid module catalog data (module dimensions for suitability / BOM). */
export function getVanDerValkPrimaryModuleGrid(system: StudioSystemType | null | undefined): ModuleGridType | null {
  const sorted = sortVanDerValkModuleGrids(system?.moduleGrids() ?? [])
  for (const grid of sorted) {
    const mod = grid.moduleType()
    if (mod != null && typeof mod.width === 'number' && typeof mod.height === 'number') {
      return grid
    }
  }
  return null
}
